import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #cartStateObserver;
  #signupObserver;
  #dismissedPopups = new WeakSet();

  #dismissSignupPopup = (records = []) => {
    if (!this.querySelector('dialog[open]')) return;

    const popups = document.querySelectorAll('[data-kl-scroll-locking-modal]');
    for (const popup of popups) {
      const changed = records.some((record) =>
        record.type === 'attributes'
          ? record.target === popup || record.target.contains(popup)
          : [...record.addedNodes].some((node) => node === popup || node.contains(popup))
      );
      if (changed) this.#dismissedPopups.delete(popup);
      let hidden = false;
      for (let element = popup; element; element = element.parentElement) {
        if (element.hidden || element.getAttribute('aria-hidden') === 'true' ||
            element.style.display === 'none' || element.style.visibility === 'hidden') {
          hidden = true;
          break;
        }
      }
      if (hidden) {
        this.#dismissedPopups.delete(popup);
        continue;
      }
      const closeButton = popup.querySelector('button.klaviyo-close-form');
      if (closeButton && !this.#dismissedPopups.has(popup)) {
        this.#dismissedPopups.add(popup);
        closeButton.click();
        this.querySelector('[ref="closeButton"]')?.focus({ preventScroll: true });
      }
    }
    this.#signupObserver.takeRecords();
  };

  #syncSignupSuppression = () => {
    this.#signupObserver.disconnect();
    this.#dismissedPopups = new WeakSet();
    if (this.querySelector('dialog[open]')) {
      this.#signupObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
      });
      this.#dismissSignupPopup();
    }
  };
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#signupObserver = new MutationObserver(this.#dismissSignupPopup);
    this.#cartStateObserver = new MutationObserver(this.#syncSignupSuppression);
    const dialog = this.querySelector('dialog');
    if (dialog) {
      this.#cartStateObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      this.#syncSignupSuppression();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cartStateObserver?.disconnect();
    this.#signupObserver?.disconnect();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
